import { useStrictLocalization } from "@faire/web/common/localization";
import { Column } from "@faire/web/slate/Layout";
import {
  getSpacing,
  Spacer,
  SpacerMultipliersKey,
} from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { FAIRE_EMPLOYEE_GROUPS } from "@faire/retailer/views/careers/__internal__/BelongingAtFaire/faireEmployeeGroups";
import {
  EyebrowText,
  LimitedSectionContent,
  Section,
} from "@faire/retailer/views/careers/__internal__/common/common";

export const BelongingEmployeeCommunities = () => {
  const headlineSpace = useResponsiveValue<SpacerMultipliersKey>({
    mobileAndAbove: "7x",
    tabletAndAbove: "10x",
  });
  return (
    <Section $withHorizontalPadding $withVerticalPadding align="center">
      <LimitedSectionContent>
        <EyebrowText>
          <StrictLocalMsg
            defaultMessage="Our employee communities"
            description={{
              text: "The heading for a section listing the company's employee run groups",
            }}
          />
        </EyebrowText>
        <Spacer height="3x" />
        <MaxWidthTypography
          variant={{
            mobileAndAbove: "displaySSerifRegular",
            tabletAndAbove: "displayMSerifRegular",
          }}
        >
          <StrictLocalMsg
            defaultMessage="Meet our employee-led resource groups, open to all Faire employees."
            description={{
              text: "The head line for a section listing the company's employee run groups",
            }}
          />
        </MaxWidthTypography>
        <Spacer height={headlineSpace} />
        <GridComponent />
      </LimitedSectionContent>
    </Section>
  );
};

const GridComponent: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const numRowsDesktopAndAbove = Math.ceil(
    FAIRE_EMPLOYEE_GROUPS(strictLocalize).length / 2
  );

  return (
    <Grid $numRowsDesktopAndAbove={numRowsDesktopAndAbove}>
      {FAIRE_EMPLOYEE_GROUPS(strictLocalize).map((group) => {
        return (
          <React.Fragment key={group.title}>
            <CellComponent
              title={group.title}
              description={group.description}
            />
          </React.Fragment>
        );
      })}
    </Grid>
  );
};

interface CellProps {
  title: string;
  description: string;
}

const CellComponent: React.FC<CellProps> = ({ title, description }) => {
  return (
    <Column>
      <Typography
        variant={{
          mobileAndAbove: "subheadingSansMedium",
          tabletAndAbove: "displaySSansRegular",
        }}
      >
        {title}
      </Typography>
      <Spacer size="1x" />
      <CellDescription
        variant={{
          mobileAndAbove: "paragraphSansRegular",
          tabletAndAbove: "subheadingSansRegular",
        }}
      >
        {description}
      </CellDescription>
    </Column>
  );
};

const MaxWidthTypography = styled(Typography)`
  max-width: ${getSpacing(430)};
`;

const Grid = styled.div<{ $numRowsDesktopAndAbove: number }>`
  display: grid;
  column-gap: ${getSpacing("9x")};
  row-gap: ${getSpacing("4x")};
  ${responsiveMediaQueries.desktopAndAbove} {
    grid-template-rows: repeat(
      ${(props) => props.$numRowsDesktopAndAbove},
      auto
    );
    grid-auto-flow: column;
    row-gap: ${getSpacing("5x")};
    margin-left: auto;
  }
`;

const CellDescription = styled(Typography)`
  max-width: ${getSpacing(368)};
  ${responsiveMediaQueries.tabletAndBelow} {
    max-width: ${getSpacing(470)};
  }
`;
